import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject, Observable, BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { takeUntil, map, switchMap, debounceTime, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { TagAppearanceET } from '@liberty-financial/ng-components/lib/components/tag/tag.enum';

import { AuditService } from '../services/audit.service';
import {
  AUDIT_ACTOR_CATEGORY_ALL,
  AuditActorCategory,
  AuditActorFilter,
  AuditLogEvent,
  EntityType
} from '../models/audit-log';

@Component({
  selector: 'app-audit-list',
  templateUrl: './audit-list.component.html',
  styleUrls: ['./audit-list.component.scss']
})
export class AuditListComponent implements OnInit, OnDestroy {
  @Input() entityType!: EntityType;
  @Input() entityId!: string;

  private readonly destroying$ = new Subject<void>();
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly search$ = new BehaviorSubject<string>('');
  private readonly actorFilter$ = new BehaviorSubject<AuditActorFilter>(AUDIT_ACTOR_CATEGORY_ALL);
  private readonly sortOrder$ = new BehaviorSubject<'newest' | 'oldest'>('newest');
  auditEvents$!: Observable<AuditLogEvent[]>;

  expandedEventId: number | null = null;
  sortOrder: 'newest' | 'oldest' = 'newest';
  searchControl: FormControl = new FormControl<string | null>(null);

  readonly actorFilterOptions: { label: string; value: AuditActorFilter }[] = [
    { label: 'All', value: AUDIT_ACTOR_CATEGORY_ALL },
    { label: 'Broker', value: 'Broker' },
    { label: 'Customer', value: 'Customer' },
    { label: 'System', value: 'System' }
  ];

  actorFilter: AuditActorFilter = AUDIT_ACTOR_CATEGORY_ALL;
  public form: FormGroup = this.formBuilder.group({
    searchControl: this.searchControl
  });
  subscriptions: Subscription[] = [];

  constructor(
    private auditService: AuditService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    // The audit log fans out to KYC and the document service, so it is fetched
    // once and cached. Search, actor and sort are applied to the cached list so
    // changing them re-renders immediately instead of waiting on the network.
    const loadedEvents$ = this.reload$.pipe(
      switchMap(() => this.auditService.getAuditLog(this.entityType, this.entityId)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.auditEvents$ = combineLatest([
      loadedEvents$,
      this.search$,
      this.actorFilter$,
      this.sortOrder$
    ]).pipe(
      map(([events, searchString, actorFilter, sortOrder]) => {
        const searched = this.filterAuditEvents(events, searchString);
        const filteredEvents = this.filterByActorCategory(searched, actorFilter);
        return this.sortAuditEvents(filteredEvents, sortOrder);
      }),
      takeUntil(this.destroying$)
    );
    this.subscribeToChanges();
  }

  /**
   * Only the Opportunity audit log carries an actor category. The Clients API
   * audit endpoints backing leads, individuals, companies and trusts do not, so
   * the control would filter every row away there.
   */
  get showActorFilter(): boolean {
    return this.entityType === EntityType.OPPORTUNITY;
  }

  get selectedActorFilter(): AuditActorFilter[] {
    return [this.actorFilter];
  }

  get hasActiveFilters(): boolean {
    return this.actorFilter !== AUDIT_ACTOR_CATEGORY_ALL || !!this.searchControl.value;
  }

  actorTagAppearance(actorCategory: AuditActorCategory): TagAppearanceET {
    switch (actorCategory) {
      case 'Broker':
        return 'info';
      case 'Customer':
        return 'success';
      default:
        return 'default';
    }
  }

  onActorFilterChange(selected: string[]): void {
    const next = (selected?.[0] as AuditActorFilter) ?? AUDIT_ACTOR_CATEGORY_ALL;
    if (next === this.actorFilter) {
      return;
    }
    this.actorFilter = next;
    this.actorFilter$.next(next);
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  toggleEvent(eventId: number): void {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  isExpanded(eventId: number): boolean {
    return this.expandedEventId === eventId;
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'newest' ? 'oldest' : 'newest';
    this.sortOrder$.next(this.sortOrder);
  }

  protected refreshList(): void {
    this.reload$.next();
  }

  private subscribeToChanges(): void {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((value: string | null) => {
          this.search$.next(value ?? '');
        })
    );
  }

  private filterAuditEvents(events: AuditLogEvent[], searchString: string): AuditLogEvent[] {
    if (!searchString || searchString.trim() === '') {
      return events;
    }
    const term = searchString.toLowerCase().trim();
    return events.filter(event =>
      event.author?.toLowerCase().includes(term) ||
      event.eventType?.toLowerCase().includes(term) ||
      event.timestamp?.toLowerCase().includes(term) ||
      JSON.stringify(event.eventDetails)?.toLowerCase().includes(term)
    );
  }

  private filterByActorCategory(events: AuditLogEvent[], actorFilter: AuditActorFilter): AuditLogEvent[] {
    if (actorFilter === AUDIT_ACTOR_CATEGORY_ALL) {
      return events;
    }
    return events.filter(event => event.actorCategory === actorFilter);
  }

  private sortAuditEvents(events: AuditLogEvent[], sortOrder: 'newest' | 'oldest'): AuditLogEvent[] {
    // Copied before sorting because the source list is cached and shared.
    return [...events].sort((a, b) => {
      // First sort by timestamp
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();

      if (timestampA !== timestampB) {
        return sortOrder === 'newest' ? timestampB - timestampA : timestampA - timestampB;
      }

      // If timestamps are equal, sort by ID as tiebreaker
      return sortOrder === 'newest' ? b.id - a.id : a.id - b.id;
    });
  }
} 